import * as api from "./client";

export type UserStatus = "verified" | "suspended" | "pending";
export type UserType = "human" | "service";

export type UserContext = "account" | "staff";

export type User = {
  id: string;
  name: string;
  email: string;
  status: UserStatus;
  type: UserType;
  context: UserContext;
  externalId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UserSibling = {
  id: string;
  context: UserContext;
  status: UserStatus;
  name: string;
  externalId: string | null;
};

export type LegacyPermission = {
  product: string;
  scope?: string;
  publication_ids?: number[];
};

export type LegacyMetadata = {
  permissions: LegacyPermission[];
  data_capabilities?: boolean;
  submission_capabilities?: boolean;
  restrict_publications_to?: number[];
};

export type OrganisationCapabilitiesPayload = {
  dataCapabilities?: boolean;
  submissionCapabilities?: boolean;
  restrictPublicationsTo?: number[];
};

export type LegacyPublication = {
  id: number;
  name: string;
};

export type Organisation = {
  id: string;
  name: string;
  status: string;
  companyId: string | null;
  memberCount?: number;
  createdAt: string;
  updatedAt: string;
  legacyMetadata?: LegacyMetadata | null;
  managedMode?: "auto" | "manual";
};

type ApiOrganisationMember = {
  user: {
    id: string;
    name: string;
    email: string;
    createdAt: string;
  };
  roles: string[];
};

export type OrganisationMember = {
  id: string;
  name: string;
  email: string;
  roles: string[];
  joinedAt: string;
};

export type OrganisationDomain = {
  id: string;
  domain: string;
  createdAt: string;
};

export type OrganisationInvite = {
  id: string;
  email: string;
  status: string;
  createdAt: string;
  expiresAt: string | null;
};

export type PlatformRoles = {
  roles: string[];
};

export type PracticeArea = { id: number; name: string };
export type ProductEntitlement = { product: string; payload: Record<string, unknown> };
export type ProductScopeRestriction = { product: string; scope_ids: string[] };

export type BreakdownLayerPermission = {
  product: string;
  publication_ids?: number[];
  scope?: string;
};

export type BreakdownAttributionLayer = "firm_entitlement" | "cxi_grant" | "UPE";

export type BreakdownAttribution = {
  product: string;
  finalPayload: object | null;
  grantedBy: Array<{
    layer: BreakdownAttributionLayer;
    payload: object;
    source?: "manual" | "reconcile";
  }>;
  wonBy: BreakdownAttributionLayer | null;
};

export type PermissionsBreakdown = {
  organisationId: string;
  organisationName: string | null;
  firmRef: string | null;
  companyId: string | null;
  layers: {
    firmEntitlement: {
      present: boolean;
      rows: Array<{
        product: string;
        region_id: string;
        phase: string;
        source: "manual" | "reconcile";
      }>;
    };
    cxiGrant: {
      present: boolean;
      practiceAreaIds: number[];
    };
    userProductEntitlements: {
      present: boolean;
      note: string;
      entries: Array<{ product: string; payload: object }>;
    };
  };
  resolved: {
    permissions: BreakdownLayerPermission[];
    capabilities: object;
    firm_ref: string | null;
  };
  attribution: BreakdownAttribution[];
};

type ApiPermissionsBreakdownResponse = {
  data: PermissionsBreakdown;
};

export type ResolvedEntitlement = {
  product: string;
  publication_ids?: number[];
};

type ApiResolvedEntitlementsResponse = {
  data: {
    metadata: {
      permissions?: ResolvedEntitlement[];
    };
    permissions: string[];
  };
};

export type FirmEntitlementRow = {
  product: string;
  regionId: string;
  phase: string;
  source: "reconcile" | "manual";
};

export type FirmPublicationRestrictionRow = {
  regionId: string;
  phase: "current" | "next";
  source: "reconcile" | "manual";
};

export type UserRoleRow = {
  role: string;
  source: "reconcile" | "manual";
  organisationId: string;
};

export type TemporalFeature = {
  feature: string;
  state: "current" | "future";
  from?: string;
  until?: string;
};

export type FirmFeaturesSource = "current" | "fulfilment";

export type FirmFeatureStates = {
  source: FirmFeaturesSource;
  temporalFeatures: TemporalFeature[];
  /**
   * The firm's entitled country/region scope ids, keyed by feature code (`live`/`referee_track`/
   * `sars`) — per-user-country-scoping-and-cxi-plan.md Stage A. Populated only under
   * source=fulfilment; empty under source=current (no fulfilment-side scope concept there). Raw
   * scope ids only — no display-name resolution yet (that's Stage D, the picker UI).
   */
  scopesByFeature: Record<string, string[]>;
};

export type SetAccountRolesPayload = {
  organisationId: string;
  roles: string[];
};

export type SetAccountRolesResult = {
  roles: string[];
};

type ApiSetAccountRolesResponse = {
  data: SetAccountRolesResult;
};

type ApiUserSiblingsResponse = {
  data: UserSibling[];
};

type ApiListResponse<T> = {
  data: T[];
  meta: { total: number; limit: number; offset: number };
};

type ApiSingleResponse<T> = {
  data: T;
};

type ApiRolesResponse = {
  data: PlatformRoles;
};

type ApiProductEntitlementsResponse = {
  data: ProductEntitlement[];
};

type ApiProductScopeRestrictionsResponse = {
  data: ProductScopeRestriction[];
};

type ApiMembersResponse = {
  data: ApiOrganisationMember[];
};

type ApiDomainsResponse = {
  data: OrganisationDomain[];
};

type ApiInvitesResponse = {
  data: OrganisationInvite[];
};

export type UsersListResult = {
  users: User[];
  total: number;
};

export type OrganisationsListResult = {
  organisations: Organisation[];
  total: number;
};

export type UsersFilters = {
  search?: string;
  status?: string;
  type?: string;
  context?: UserContext;
  limit?: number;
  offset?: number;
};

export type CreateUserPayload = {
  name: string;
  email: string;
  context: UserContext;
  status?: "unverified" | "verified";
  platformRoles?: { staff: string[] };
  /** Required for account-context users: links the user to an existing organisation. */
  organisationId?: string;
};

export type CreateUserResult = {
  id: string;
  name: string;
  email: string;
};

export type OrganisationsFilters = {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

export type CreateOrganisationPayload = {
  name: string;
  companyId?: string;
};

export type UpdateOrganisationPayload = {
  name?: string;
  companyId?: string | null;
  status?: "active" | "inactive";
};

export type UserOrganisationWithRoles = {
  id: string;
  name: string;
  status: string;
  companyId: string | null;
  roles: string[];
};

type ApiUserOrganisationsResponse = {
  data: UserOrganisationWithRoles[];
};

function buildQueryString(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      searchParams.append(key, String(value));
    }
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export const userManagement = {
  getUsers: async (filters?: UsersFilters): Promise<UsersListResult> => {
    const query = buildQueryString({
      search: filters?.search,
      status: filters?.status,
      type: filters?.type,
      context: filters?.context,
      limit: filters?.limit,
      offset: filters?.offset,
    });
    const response = await api.get<ApiListResponse<User>>(`/v1/users${query}`);
    return { users: response.data, total: response.meta.total };
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await api.get<ApiSingleResponse<User>>(`/v1/users/${id}`);
    return response.data;
  },

  createUser: async (payload: CreateUserPayload): Promise<CreateUserResult> => {
    const response = await api.post<ApiSingleResponse<CreateUserResult>>("/v1/users", payload);
    return response.data;
  },

  getUserPlatformRoles: async (id: string): Promise<string[]> => {
    const response = await api.get<ApiRolesResponse>(`/v1/users/${id}/platform-roles`);
    return response.data.roles;
  },

  setUserPlatformRoles: async (id: string, context: string, roles: string[]): Promise<void> => {
    await api.put(`/v1/users/${id}/platform-roles`, { context, roles });
  },

  getOrganisations: async (filters?: OrganisationsFilters): Promise<OrganisationsListResult> => {
    const query = buildQueryString({
      search: filters?.search,
      status: filters?.status,
      limit: filters?.limit,
      offset: filters?.offset,
    });
    const response = await api.get<ApiListResponse<Organisation>>(`/v1/organisations${query}`);
    return { organisations: response.data, total: response.meta.total };
  },

  getOrganisationById: async (id: string): Promise<Organisation> => {
    const response = await api.get<ApiSingleResponse<Organisation>>(`/v1/organisations/${id}`);
    return response.data;
  },

  createOrganisation: async (payload: CreateOrganisationPayload): Promise<Organisation> => {
    const response = await api.post<ApiSingleResponse<Organisation>>("/v1/organisations", payload);
    return response.data;
  },

  updateOrganisation: async (
    orgId: string,
    payload: UpdateOrganisationPayload,
  ): Promise<Organisation> => {
    const response = await api.patch<ApiSingleResponse<Organisation>>(
      `/v1/organisations/${orgId}`,
      payload,
    );
    return response.data;
  },

  getOrganisationMembers: async (orgId: string, search?: string): Promise<OrganisationMember[]> => {
    const query = search ? `?search=${encodeURIComponent(search)}` : "";
    const response = await api.get<ApiMembersResponse>(
      `/v1/organisations/${orgId}/members${query}`,
    );
    return response.data.map(apiMember => ({
      id: apiMember.user.id,
      name: apiMember.user.name,
      email: apiMember.user.email,
      roles: apiMember.roles,
      joinedAt: apiMember.user.createdAt,
    }));
  },

  getOrganisationDomains: async (orgId: string): Promise<OrganisationDomain[]> => {
    const response = await api.get<ApiDomainsResponse>(`/v1/organisations/${orgId}/domains`);
    return response.data;
  },

  getOrganisationInvites: async (orgId: string): Promise<OrganisationInvite[]> => {
    const response = await api.get<ApiInvitesResponse>(`/v1/organisations/${orgId}/invites`);
    return response.data;
  },

  revokeInvite: async (orgId: string, inviteId: string): Promise<void> => {
    await api.del(`/v1/organisations/${orgId}/invites/${inviteId}`);
  },

  setAccountRoles: async (
    userId: string,
    payload: SetAccountRolesPayload,
  ): Promise<SetAccountRolesResult> => {
    const response = await api.post<ApiSetAccountRolesResponse>(
      `/v1/frontegg-users/${userId}/roles`,
      payload,
    );
    return response.data;
  },

  getUserSiblingsByEmail: async (email: string): Promise<UserSibling[]> => {
    const response = await api.post<ApiUserSiblingsResponse>("/v1/users/by-email", { email });
    return response.data;
  },

  getUserOrganisations: async (userId: string): Promise<UserOrganisationWithRoles[]> => {
    const response = await api.get<ApiUserOrganisationsResponse>(
      `/v1/users/${userId}/organisations`,
    );
    return response.data;
  },

  updateUser: async (
    userId: string,
    payload: { name?: string; externalId?: string | null },
  ): Promise<User> => {
    const response = await api.patch<ApiSingleResponse<User>>(`/v1/users/${userId}`, payload);
    return response.data;
  },

  addOrganisationMember: async (orgId: string, userId: string): Promise<void> => {
    await api.post(`/v1/organisations/${orgId}/members`, { userId });
  },

  removeOrganisationMember: async (orgId: string, userId: string): Promise<void> => {
    await api.del(`/v1/organisations/${orgId}/members/${userId}`);
  },

  getLegacyPublications: async (): Promise<LegacyPublication[]> => {
    const response = await api.get<{
      publications: { id: number; publication: string }[];
      pagination?: unknown;
    }>("/v1/taxonomy/legacy-publications");
    return response.publications.map(p => ({ id: p.id, name: p.publication }));
  },

  setOrganisationPublications: async (
    orgId: string,
    product: string,
    publicationIds: number[],
  ): Promise<void> => {
    await api.put(`/v1/organisations/${orgId}/legacy-metadata/publications`, {
      product,
      publicationIds,
    });
  },

  setOrganisationCapabilities: async (
    orgId: string,
    payload: OrganisationCapabilitiesPayload,
  ): Promise<void> => {
    await api.put(`/v1/organisations/${orgId}/legacy-metadata/capabilities`, payload);
  },

  setOrganisationManagedMode: async (orgId: string, mode: "auto" | "manual"): Promise<void> => {
    await api.put(`/v1/organisations/${orgId}/managed-mode`, { mode });
  },

  removeOrganisationProduct: async (orgId: string, product: string): Promise<void> => {
    await api.del(
      `/v1/organisations/${orgId}/legacy-metadata/products/${encodeURIComponent(product)}`,
    );
  },

  getUserProductEntitlements: async (
    userId: string,
    orgId: string,
  ): Promise<ProductEntitlement[]> => {
    const response = await api.get<ApiProductEntitlementsResponse>(
      `/v1/users/${userId}/organisations/${orgId}/product-entitlements`,
    );
    return response.data;
  },

  setUserProductEntitlement: async (
    userId: string,
    orgId: string,
    product: string,
    payload: Record<string, unknown>,
  ): Promise<void> => {
    await api.put(
      `/v1/users/${userId}/organisations/${orgId}/product-entitlements/${encodeURIComponent(product)}`,
      { payload },
    );
  },

  deleteUserProductEntitlement: async (
    userId: string,
    orgId: string,
    product: string,
  ): Promise<void> => {
    await api.del(
      `/v1/users/${userId}/organisations/${orgId}/product-entitlements/${encodeURIComponent(product)}`,
    );
  },

  getUserProductScopeRestrictions: async (
    userId: string,
    orgId: string,
  ): Promise<ProductScopeRestriction[]> => {
    const response = await api.get<ApiProductScopeRestrictionsResponse>(
      `/v1/users/${userId}/organisations/${orgId}/scope-restrictions`,
    );
    return response.data;
  },

  setUserProductScopeRestriction: async (
    userId: string,
    orgId: string,
    product: string,
    scopeIds: string[],
  ): Promise<void> => {
    await api.put(
      `/v1/users/${userId}/organisations/${orgId}/scope-restrictions/${encodeURIComponent(product)}`,
      { scope_ids: scopeIds },
    );
  },

  deleteUserProductScopeRestriction: async (
    userId: string,
    orgId: string,
    product: string,
  ): Promise<void> => {
    await api.del(
      `/v1/users/${userId}/organisations/${orgId}/scope-restrictions/${encodeURIComponent(product)}`,
    );
  },

  getFirmEntitlement: async (orgId: string): Promise<FirmEntitlementRow[]> => {
    const response = await api.get<{ data: FirmEntitlementRow[] }>(
      `/v1/organisations/${orgId}/firm-entitlement`,
    );
    return response.data;
  },

  addFirmEntitlementRow: async (
    orgId: string,
    row: { product: string; regionId: string; phase: string },
  ): Promise<void> => {
    await api.post(`/v1/organisations/${orgId}/firm-entitlement`, row);
  },

  getFirmPublicationRestriction: async (
    orgId: string,
  ): Promise<FirmPublicationRestrictionRow[]> => {
    const response = await api.get<{ data: FirmPublicationRestrictionRow[] }>(
      `/v1/organisations/${orgId}/firm-publication-restriction`,
    );
    return response.data;
  },

  getUserRoles: async (userId: string): Promise<UserRoleRow[]> => {
    const response = await api.get<{ data: UserRoleRow[] }>(`/v1/users/${userId}/roles`);
    return response.data;
  },

  addUserRole: async (userId: string, role: string, organisationId: string): Promise<void> => {
    await api.post(`/v1/users/${userId}/roles`, { role, organisationId });
  },

  removeUserRole: async (userId: string, role: string, organisationId: string): Promise<void> => {
    await api.del(
      `/v1/users/${userId}/roles/${encodeURIComponent(role)}?organisationId=${organisationId}`,
    );
  },

  getOrganisationFeatureStates: async (orgId: string): Promise<FirmFeatureStates> => {
    const response = await api.get<{
      source?: FirmFeaturesSource;
      temporalFeatures: TemporalFeature[];
      scopesByFeature?: Record<string, string[]>;
    }>(`/v1/organisations/${orgId}/feature-states`);
    return {
      source: response.source ?? "current",
      temporalFeatures: response.temporalFeatures ?? [],
      scopesByFeature: response.scopesByFeature ?? {},
    };
  },

  mintImpersonationToken: async (userId: string): Promise<{ token: string; expiresAt: string }> => {
    return api.post<{ token: string; expiresAt: string }>("/v1/impersonation/account-token", {
      userId,
    });
  },

  getUserResolvedEntitlements: async (
    userId: string,
    orgId: string,
  ): Promise<ResolvedEntitlement[]> => {
    const response = await api.get<ApiResolvedEntitlementsResponse>(
      `/v1/users/${userId}/permissions?organisationId=${orgId}`,
    );
    return response.data.metadata.permissions ?? [];
  },

  getUserPermissionsBreakdown: async (
    userId: string,
    orgId: string,
  ): Promise<PermissionsBreakdown> => {
    const response = await api.get<ApiPermissionsBreakdownResponse>(
      `/v1/users/${userId}/permissions/breakdown?organisationId=${orgId}`,
    );
    return response.data;
  },
};
